import { motion } from 'motion/react'
import { ChevronRight, CornerDownRight, GripVertical, Plus } from 'lucide-react'
import type { NotebookNote } from '../../../shared/types/notebook'
import type { DropPosition } from '../dndTree'
import DropIndicator from './DropIndicator'

interface PageRowProps {
  note: NotebookNote
  depth?: number
  isExpanded?: boolean
  isActive?: boolean
  hasChildren?: boolean
  isDragging?: boolean
  isDropTarget?: boolean
  dropPosition?: DropPosition | null
  onToggleExpand?: () => void
  onNavigate: (id: string) => void
  onCreateChild?: (parentId: string) => void
}

export default function PageRow({
  note,
  depth = 0,
  isExpanded = false,
  isActive = false,
  hasChildren = false,
  isDragging = false,
  isDropTarget = false,
  dropPosition = null,
  onToggleExpand,
  onNavigate,
  onCreateChild,
}: PageRowProps) {
  const isPending = note.tags.includes('_pending')

  return (
    <div
      className={`relative flex items-center group rounded-lg transition-colors ${
        isDragging
          ? 'opacity-30'
          : isDropTarget && dropPosition === 'inside'
            ? 'bg-gold/15 ring-2 ring-gold/50 shadow-[0_0_12px_rgba(200,149,48,0.2)]'
            : isActive
              ? 'bg-gold/10'
              : 'hover:bg-white/[0.03]'
      }`}
      style={{ paddingLeft: `${depth * 24}px` }}
    >
      {/* Drop indicator lines */}
      {isDropTarget && dropPosition && dropPosition !== 'inside' && (
        <DropIndicator position={dropPosition} depth={depth} />
      )}

      {/* Drag grip icon (visual hint) */}
      <div className="w-5 h-7 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical size={12} className="text-pearl/55" />
      </div>

      {/* Chevron */}
      <button
        onClick={e => {
          e.stopPropagation()
          onToggleExpand?.()
        }}
        className="w-7 h-7 flex items-center justify-center flex-shrink-0"
      >
        {hasChildren ? (
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.15 }}
          >
            <ChevronRight size={14} className="text-pearl/60" />
          </motion.div>
        ) : (
          <span className="w-3.5" />
        )}
      </button>

      {/* Emoji + Title — tap to navigate */}
      <button
        onClick={() => !isPending && onNavigate(note.id)}
        className={`flex items-center gap-2.5 flex-1 min-w-0 py-2.5 pr-2 text-left ${isPending ? 'cursor-default' : ''}`}
      >
        <span className="text-base flex-shrink-0">{note.icon}</span>
        {isPending ? (
          <span className="flex items-center gap-2 text-pearl/60 text-sm">
            <span className="inline-block w-3 h-3 border-2 border-gold/40 border-t-gold rounded-full animate-spin" />
            <span className="truncate">{note.title}</span>
          </span>
        ) : (
          <span className="text-ivory text-sm truncate">{note.title || 'Untitled'}</span>
        )}
      </button>

      {/* Nest indicator — visible when dropping inside */}
      {isDropTarget && dropPosition === 'inside' && (
        <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 text-gold/70">
          <CornerDownRight size={14} />
        </div>
      )}

      {/* Add child button — visible on expanded parents */}
      {isExpanded && onCreateChild && !(isDropTarget && dropPosition === 'inside') && (
        <button
          onClick={e => {
            e.stopPropagation()
            onCreateChild(note.id)
          }}
          className={`w-7 h-7 flex items-center justify-center transition-opacity text-pearl/55 hover:text-gold ${
            isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          <Plus size={14} />
        </button>
      )}
    </div>
  )
}
